import type { FunctionComponent } from 'react'
import React, { useEffect, useState } from 'react'
import { Duration } from 'luxon'
import { tokens, FontIcon } from '@arvoreeducacao/bonsai-ds'
import {
  ContainerOnlySmallScreens,
  ContainerOnlyBigScreens
} from 'styles/common/Common.styles'
import * as playerActions from 'context/player/reducer/player.actions'
import {
  Modal,
  ModalContent,
  BookBox,
  ChaptersBox,
  BookCover,
  Title,
  List,
  ChapterTime,
  ListItem,
  ChapterRight,
  ChapterLeft,
  CloseModal,
  ContainerOnlyBigScreensStyled,
  CheckCircleFill
} from './PlayerChapters.styles'
import { usePlayerContext } from 'context/player'
import { useTranslation } from 'i18n'
import useA11yEnabled from 'utils/hooks/use-a11y-enabled'

const PlayerChapters: FunctionComponent = () => {
  const [isDesktop, setDesktop] = useState(
    window.innerWidth > tokens.sizes.mdScreen
  )
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const {
    dispatch,
    state: { imageUrlThumb, name, audioBooks, showChaptersList, slug }
  } = usePlayerContext()
  const { t } = useTranslation()
  const { a11yEnabled } = useA11yEnabled()

  const handleResize = () => {
    setDesktop(window.innerWidth > tokens.sizes.mdScreen)
  }

  const selectChapter = id => {
    if (!isDesktop) {
      dispatch(playerActions.closeChaptersList())
    }
    dispatch(playerActions.selectChapter(id))
    dispatch(playerActions.play())
  }

  const handleChapterKeyDown = (event: React.KeyboardEvent, id) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectChapter(id)
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize, false)

    return () => {
      window.removeEventListener('resize', handleResize, false)
      dispatch(playerActions.closeChaptersList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!showChaptersList) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(playerActions.closeChaptersList())
      }
    }

    document.addEventListener('keydown', handleEscape)
    dialogRef.current?.focus()

    return () => document.removeEventListener('keydown', handleEscape)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChaptersList])

  return (
    <Modal
      data-testid="player-chapters-list"
      show={showChaptersList}
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={name ? t('Capítulos de {{name}}', { name }) : t('Capítulos')}
    >
      <ModalContent>
        <ContainerOnlyBigScreensStyled>
          <BookBox>
            <BookCover alt={`capa-do-livro-${slug}`} src={imageUrlThumb} />
          </BookBox>
        </ContainerOnlyBigScreensStyled>
        <ChaptersBox>
          <ContainerOnlySmallScreens>
            <CloseModal>
              <FontIcon
                data-testid="player-close-chapters"
                role="button"
                tabIndex={0}
                aria-label={t('Fechar lista de capítulos')}
                onClick={() => dispatch(playerActions.closeChaptersList())}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    dispatch(playerActions.closeChaptersList())
                  }
                }}
                iconName="cancel"
                size={12}
                color={tokens.colors.black4}
              />
            </CloseModal>
          </ContainerOnlySmallScreens>
          <ContainerOnlyBigScreens>
            <Title>{name}</Title>
          </ContainerOnlyBigScreens>
          <ContainerOnlySmallScreens>
            <Title>{t('Capítulos')}</Title>
          </ContainerOnlySmallScreens>
          <List>
            {audioBooks.map(audio => (
              <ListItem
                key={audio.title}
                data-testid={
                  audio.active ? 'player-list-item-active' : 'player-list-item'
                }
                active={audio.active}
                a11yEnabled={a11yEnabled}
                role="button"
                tabIndex={0}
                aria-current={audio.active ? 'true' : undefined}
                onKeyDown={
                  !audio.active
                    ? event => handleChapterKeyDown(event, audio.id)
                    : undefined
                }
                onClick={
                  !audio.active
                    ? () => {
                        selectChapter(audio.id)
                      }
                    : undefined
                }
              >
                <ChapterLeft>{audio.title}</ChapterLeft>
                <ChapterRight>
                  <ChapterTime data-testid="player-chapters-time">
                    {Duration.fromMillis(audio.duration * 1000).toFormat(
                      audio.duration >= 3600 ? 'hh:mm:ss' : 'mm:ss'
                    )}
                  </ChapterTime>
                  <CheckCircleFill
                    iconName="check-circle-fill"
                    size={20}
                    color={
                      a11yEnabled
                        ? tokens.colors.primary900
                        : tokens.colors.primary
                    }
                    show={audio.hasListened}
                  />
                </ChapterRight>
              </ListItem>
            ))}
          </List>
        </ChaptersBox>
      </ModalContent>
    </Modal>
  )
}

export default PlayerChapters
